import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import {
  CreateInsuranceProviderPayload,
  InsuranceApiService,
  InsuranceProviderContract,
  InsuranceProviderDetail,
  InsuranceRatingPlan,
  UpdateInsuranceProviderPayload
} from './insurance-api.service';
import {
  INSURANCE_HEALTH_COVERED_SERVICE_FIELDS,
  coveredServiceItemFromPayload,
  coveredServiceSummary,
  emptyCoveredServiceItem,
  parseCoveredServicesJson,
  serializeCoveredServicesJson
} from './insurance-group-schemas';

type ProviderContractDraft = InsuranceProviderContract & {
  coveredServices: Record<string, unknown>[];
};

@Component({
  standalone: true,
  selector: 'ain-insurance-health-provider-detail',
  imports: [
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    JalaliDateInputComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    SelectModule,
    TableModule,
    PartySelectorComponent,
    ButtonModule,
    DialogModule,
    StructuredJsonFormComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.health.providerDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.health.providers.help.detail.title'"
        [descriptionKey]="'insurance.health.providers.help.detail.description'"
        [bulletKeys]="[
          'insurance.health.providers.help.detail.bullets.profile',
          'insurance.health.providers.help.detail.bullets.contracts',
          'insurance.health.providers.help.detail.bullets.party'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else if (isCreateMode()) {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/health/providers']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveCreate()"></button>
        </div>
        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.health.providers.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.health.providers.help.info.title'"
              [descriptionKey]="'insurance.health.providers.help.info.description'"
              [bulletKeys]="[
                'insurance.health.providers.help.info.bullets.legalEntity',
                'insurance.health.providers.help.info.bullets.network',
                'insurance.health.providers.help.info.bullets.status'
              ]" />
          </div>
          <form class="grid" (ngSubmit)="saveCreate()">
            <label>
              <span>{{ t('insurance.health.providers.fields.legalEntityId') }}</span>
              <ain-party-selector [(ngModel)]="createForm.legalEntityId" name="legalEntityId" />
            </label>
            <label>
              <span>{{ t('insurance.health.providers.fields.code') }}</span>
              <input pInputText [(ngModel)]="createForm.code" name="code" />
            </label>
            <label>
              <span>{{ t('insurance.health.providers.fields.name') }}</span>
              <input pInputText [(ngModel)]="createForm.name" name="name" />
            </label>
            <label>
              <span>{{ t('insurance.health.providers.fields.providerType') }}</span>
              <p-select
                [options]="providerTypeOptions"
                optionValue="value"
                [(ngModel)]="createForm.providerType"
                name="providerType"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.health.providers.fields.partyId') }}</span>
              <ain-party-selector [(ngModel)]="createForm.partyId" name="partyId" />
            </label>
            <label>
              <span>{{ t('insurance.health.providers.fields.networkTier') }}</span>
              <p-select
                [options]="networkTierOptions"
                optionValue="value"
                [(ngModel)]="createForm.networkTier"
                name="networkTier"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.health.providers.fields.contractStatus') }}</span>
              <p-select
                [options]="contractStatusOptions"
                optionValue="value"
                [(ngModel)]="createForm.contractStatus"
                name="contractStatus"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
          </form>
        </section>
        @if (error()) { <p class="error">{{ error() }}</p> }
      } @else if (!provider()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/health/providers']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveEdit()"></button>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.health.providers.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.health.providers.help.info.title'"
              [descriptionKey]="'insurance.health.providers.help.info.description'"
              [bulletKeys]="[
                'insurance.health.providers.help.info.bullets.legalEntity',
                'insurance.health.providers.help.info.bullets.network',
                'insurance.health.providers.help.info.bullets.status'
              ]" />
          </div>
          <div class="summary">
            <span>{{ t('insurance.health.providers.fields.code') }}: <strong>{{ provider()!.code }}</strong></span>
            <span>{{ t('insurance.health.providers.fields.providerType') }}: <strong>{{ t(providerTypeKey(provider()!.providerType)) }}</strong></span>
            <span>{{ t('insurance.health.providers.fields.networkTier') }}: <strong>{{ t(networkTierKey(provider()!.networkTier)) }}</strong></span>
            <span>{{ t('insurance.health.providers.fields.contractStatus') }}: <strong>{{ t(contractStatusKey(provider()!.contractStatus)) }}</strong></span>
          </div>
          <form class="grid" (ngSubmit)="saveEdit()">
            <label>
              <span>{{ t('insurance.health.providers.fields.name') }}</span>
              <input pInputText [(ngModel)]="editForm.name" name="editName" />
            </label>
            <label>
              <span>{{ t('insurance.health.providers.fields.providerType') }}</span>
              <p-select
                [options]="providerTypeOptions"
                optionValue="value"
                [(ngModel)]="editForm.providerType"
                name="editProviderType"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.health.providers.fields.partyId') }}</span>
              <ain-party-selector [(ngModel)]="editForm.partyId" name="editPartyId" />
            </label>
            <label>
              <span>{{ t('insurance.health.providers.fields.networkTier') }}</span>
              <p-select
                [options]="networkTierOptions"
                optionValue="value"
                [(ngModel)]="editForm.networkTier"
                name="editNetworkTier"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.health.providers.fields.contractStatus') }}</span>
              <p-select
                [options]="contractStatusOptions"
                optionValue="value"
                [(ngModel)]="editForm.contractStatus"
                name="editContractStatus"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
          </form>
        </section>

        <section class="card">
          <div class="lines-header">
            <h3>{{ t('insurance.health.providers.sections.contracts') }}</h3>
            <div class="lines-actions">
              <ain-info-hint
                [titleKey]="'insurance.health.providers.help.contracts.title'"
                [descriptionKey]="'insurance.health.providers.help.contracts.description'"
                [bulletKeys]="[
                  'insurance.health.providers.help.contracts.bullets.dates',
                  'insurance.health.providers.help.contracts.bullets.tariff',
                  'insurance.health.providers.help.contracts.bullets.services'
                ]" />
              <button pButton type="button" [label]="t('insurance.health.providerDetail.contracts.add')" (click)="openContractDialog()"></button>
              <button pButton type="button" severity="secondary" [label]="t('common.save')" [disabled]="saving()" (click)="saveContracts()"></button>
            </div>
          </div>
          @if (contracts().length === 0) {
            <p>{{ t('insurance.health.providerDetail.contracts.empty') }}</p>
          } @else {
            <p-table [value]="contracts()" dataKey="id" styleClass="lines-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('insurance.health.providerDetail.contracts.effectiveFrom') }}</th>
                  <th>{{ t('insurance.health.providerDetail.contracts.effectiveTo') }}</th>
                  <th>{{ t('insurance.health.providerDetail.contracts.tariffScheduleId') }}</th>
                  <th>{{ t('insurance.health.providerDetail.contracts.fields.servicesCount') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                <tr>
                  <td>{{ row.effectiveFrom | ainDate }}</td>
                  <td>{{ row.effectiveTo | ainDate }}</td>
                  <td>{{ tariffLabel(row.tariffScheduleId) }}</td>
                  <td>{{ row.coveredServices.length }}</td>
                  <td class="actions-inline">
                    <button pButton type="button" size="small" severity="secondary" [label]="t('common.edit')" (click)="openContractDialog(rowIndex)"></button>
                    <button pButton type="button" size="small" severity="danger" [label]="t('common.remove')" (click)="removeContract(rowIndex)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        @if (error()) { <p class="error">{{ error() }}</p> }

        <p-dialog
          [header]="contractDialogIndex() === null ? t('insurance.health.providerDetail.contracts.dialog.add') : t('insurance.health.providerDetail.contracts.dialog.edit')"
          [modal]="true"
          [(visible)]="contractDialogVisible"
          [style]="{ width: '760px' }">
          <div class="dialog-grid">
            <label>
              <span>{{ t('insurance.health.providerDetail.contracts.effectiveFrom') }}</span>
              <ain-date-input [(ngModel)]="contractDraft.effectiveFrom" name="contractEffectiveFrom" />
            </label>
            <label>
              <span>{{ t('insurance.health.providerDetail.contracts.effectiveTo') }}</span>
              <ain-date-input [(ngModel)]="contractDraft.effectiveTo" name="contractEffectiveTo" />
            </label>
            <label class="wide">
              <span>{{ t('insurance.health.providerDetail.contracts.tariffScheduleId') }}</span>
              <p-select
                [options]="tariffOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="contractDraft.tariffScheduleId"
                name="contractTariffScheduleId"
                [filter]="true"
                [showClear]="true"
                appendTo="body" />
            </label>
          </div>
          <div class="lines-header section-gap">
            <h4>{{ t('insurance.health.providerDetail.contracts.coveredServicesJson') }}</h4>
            <button pButton type="button" size="small" [label]="t('insurance.health.providerDetail.contracts.services.add')" (click)="openServiceDialog()"></button>
          </div>
          @if (contractDraft.coveredServices.length === 0) {
            <p>{{ t('insurance.health.providerDetail.contracts.services.empty') }}</p>
          } @else {
            <p-table [value]="contractDraft.coveredServices" styleClass="lines-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('insurance.health.schemas.coveredService.code') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                <tr>
                  <td>{{ coveredServiceSummary(row) }}</td>
                  <td class="actions-inline">
                    <button pButton type="button" size="small" severity="secondary" [label]="t('common.edit')" (click)="openServiceDialog(rowIndex)"></button>
                    <button pButton type="button" size="small" severity="danger" [label]="t('common.remove')" (click)="removeService(rowIndex)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="contractDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" [disabled]="!contractDraft.tariffScheduleId" (click)="saveContractDialog()"></button>
          </ng-template>
        </p-dialog>

        <p-dialog
          [header]="serviceDialogIndex() === null ? t('insurance.health.providerDetail.contracts.services.add') : t('common.edit')"
          [modal]="true"
          [(visible)]="serviceDialogVisible"
          [style]="{ width: '640px' }">
          <ain-structured-json-form
            [fields]="coveredServiceFields"
            [(payloadJson)]="servicePayloadJson" />
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="serviceDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="saveServiceDialog()"></button>
          </ng-template>
        </p-dialog>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3 { margin: 0; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 10px; }
    .lines-header h3, .lines-header h4 { margin: 0; }
    .lines-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; font-size: 14px; margin-bottom: 10px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid .wide { grid-column: 1 / -1; }
    .section-gap { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
    label { display: grid; gap: 6px; }
    .actions-inline { display: flex; gap: 6px; justify-content: flex-end; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) {
      .grid, .dialog-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsuranceHealthProviderDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly provider = signal<InsuranceProviderDetail | null>(null);
  readonly contracts = signal<ProviderContractDraft[]>([]);
  readonly isCreateMode = signal(false);
  readonly ratingPlans = signal<InsuranceRatingPlan[]>([]);
  readonly contractDialogVisible = signal(false);
  readonly contractDialogIndex = signal<number | null>(null);
  readonly serviceDialogVisible = signal(false);
  readonly serviceDialogIndex = signal<number | null>(null);
  readonly coveredServiceFields = INSURANCE_HEALTH_COVERED_SERVICE_FIELDS;

  contractDraft: ProviderContractDraft = this.emptyContractDraft();
  servicePayloadJson = '{}';

  readonly providerTypeOptions = [
    { value: 'Hospital', labelKey: 'insurance.health.providers.enums.providerType.hospital' },
    { value: 'Clinic', labelKey: 'insurance.health.providers.enums.providerType.clinic' },
    { value: 'Lab', labelKey: 'insurance.health.providers.enums.providerType.lab' },
    { value: 'Pharmacy', labelKey: 'insurance.health.providers.enums.providerType.pharmacy' },
    { value: 'Physician', labelKey: 'insurance.health.providers.enums.providerType.physician' }
  ];

  readonly networkTierOptions = [
    { value: 'Preferred', labelKey: 'insurance.health.providers.enums.networkTier.preferred' },
    { value: 'Standard', labelKey: 'insurance.health.providers.enums.networkTier.standard' },
    { value: 'OutOfNetwork', labelKey: 'insurance.health.providers.enums.networkTier.outOfNetwork' }
  ];

  readonly contractStatusOptions = [
    { value: 'Active', labelKey: 'insurance.health.providers.enums.contractStatus.active' },
    { value: 'Suspended', labelKey: 'insurance.health.providers.enums.contractStatus.suspended' },
    { value: 'Expired', labelKey: 'insurance.health.providers.enums.contractStatus.expired' }
  ];

  createForm: CreateInsuranceProviderPayload = {
    legalEntityId: '',
    code: '',
    name: '',
    providerType: 'Hospital',
    partyId: '',
    networkTier: 'Standard',
    contractStatus: 'Active'
  };

  editForm: UpdateInsuranceProviderPayload = {
    name: '',
    providerType: 'Hospital',
    partyId: '',
    networkTier: 'Standard',
    contractStatus: 'Active'
  };

  ngOnInit(): void {
    this.api.listRatingPlans().subscribe({
      next: rows => this.ratingPlans.set(rows),
      error: () => this.ratingPlans.set([])
    });

    const providerId = this.route.snapshot.paramMap.get('providerId');
    const isNewRoute = this.route.snapshot.url.at(-1)?.path === 'new';
    if (isNewRoute) {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }

    if (!providerId) {
      this.loading.set(false);
      return;
    }

    this.api.getHealthProvider(providerId).subscribe({
      next: item => {
        this.provider.set(item);
        if (item) {
          this.editForm = {
            name: item.name,
            providerType: item.providerType,
            partyId: item.partyId,
            networkTier: item.networkTier,
            contractStatus: item.contractStatus
          };
          this.contracts.set(item.contracts.map(contract => this.toContractDraft(contract)));
        }
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.permissionDenied.set(err?.status === 403);
        this.loading.set(false);
      }
    });
  }

  pageTitle(): string {
    return this.isCreateMode()
      ? this.t('insurance.health.providerCreate.title')
      : this.t('insurance.health.providerDetail.title');
  }

  tariffOptions(): Array<{ label: string; value: string }> {
    return this.ratingPlans().map(plan => ({
      value: plan.id,
      label: `${plan.code} — ${plan.name}`
    }));
  }

  tariffLabel(tariffScheduleId: string): string {
    return this.tariffOptions().find(item => item.value === tariffScheduleId)?.label ?? tariffScheduleId;
  }

  coveredServiceSummary(item: Record<string, unknown>): string {
    return coveredServiceSummary(item);
  }

  openContractDialog(index: number | null = null): void {
    this.contractDialogIndex.set(index);
    if (index === null) {
      this.contractDraft = this.emptyContractDraft();
    } else {
      const current = this.contracts()[index];
      this.contractDraft = {
        ...current,
        coveredServices: current.coveredServices.map(item => ({ ...item }))
      };
    }
    this.contractDialogVisible.set(true);
  }

  saveContractDialog(): void {
    const index = this.contractDialogIndex();
    const draft: ProviderContractDraft = {
      ...this.contractDraft,
      coveredServicesJson: serializeCoveredServicesJson(this.contractDraft.coveredServices)
    };
    if (index === null) {
      this.contracts.set([...this.contracts(), draft]);
    } else {
      this.contracts.set(this.contracts().map((item, itemIndex) => itemIndex === index ? draft : item));
    }
    this.contractDialogVisible.set(false);
  }

  removeContract(index: number): void {
    this.contracts.set(this.contracts().filter((_, itemIndex) => itemIndex !== index));
  }

  openServiceDialog(index: number | null = null): void {
    this.serviceDialogIndex.set(index);
    if (index === null) {
      this.servicePayloadJson = JSON.stringify(emptyCoveredServiceItem());
    } else {
      this.servicePayloadJson = JSON.stringify(this.contractDraft.coveredServices[index]);
    }
    this.serviceDialogVisible.set(true);
  }

  saveServiceDialog(): void {
    const item = coveredServiceItemFromPayload(this.servicePayloadJson);
    const index = this.serviceDialogIndex();
    if (index === null) {
      this.contractDraft.coveredServices = [...this.contractDraft.coveredServices, item];
    } else {
      this.contractDraft.coveredServices = this.contractDraft.coveredServices.map((row, rowIndex) =>
        rowIndex === index ? item : row
      );
    }
    this.serviceDialogVisible.set(false);
  }

  removeService(index: number): void {
    this.contractDraft.coveredServices = this.contractDraft.coveredServices.filter((_, rowIndex) => rowIndex !== index);
  }

  saveCreate(): void {
    this.saving.set(true);
    this.error.set('');
    this.api.createHealthProvider(this.createForm).subscribe({
      next: item => {
        this.saving.set(false);
        void this.router.navigate(['/insurance/health/providers', item.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  saveEdit(): void {
    const current = this.provider();
    if (!current) return;
    this.saving.set(true);
    this.error.set('');
    this.api.updateHealthProvider(current.id, this.editForm).subscribe({
      next: item => {
        this.provider.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  saveContracts(): void {
    const current = this.provider();
    if (!current) return;
    this.saving.set(true);
    this.error.set('');
    this.api.upsertHealthProviderContracts(current.id, {
      items: this.contracts().map(item => ({
        effectiveFrom: item.effectiveFrom,
        effectiveTo: item.effectiveTo,
        tariffScheduleId: item.tariffScheduleId,
        coveredServicesJson: serializeCoveredServicesJson(item.coveredServices)
      }))
    }).subscribe({
      next: rows => {
        this.contracts.set(rows.map(contract => this.toContractDraft(contract)));
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  providerTypeKey(value: string): string {
    return `insurance.health.providers.enums.providerType.${value.charAt(0).toLowerCase()}${value.slice(1)}`;
  }

  networkTierKey(value: string): string {
    return `insurance.health.providers.enums.networkTier.${value.charAt(0).toLowerCase()}${value.slice(1)}`;
  }

  contractStatusKey(value: string): string {
    return `insurance.health.providers.enums.contractStatus.${value.charAt(0).toLowerCase()}${value.slice(1)}`;
  }

  private emptyContractDraft(): ProviderContractDraft {
    return {
      id: '',
      providerId: this.provider()?.id ?? '',
      effectiveFrom: new Date().toISOString(),
      effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      tariffScheduleId: '',
      coveredServicesJson: '[]',
      coveredServices: []
    };
  }

  private toContractDraft(contract: InsuranceProviderContract): ProviderContractDraft {
    return {
      ...contract,
      coveredServices: parseCoveredServicesJson(contract.coveredServicesJson)
    };
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
